import type { GeoJSONSource, Map as MapboxMap } from "mapbox-gl";
import type { FeatureCollection } from "geojson";
import { wrapLng } from "./geo";

const FIT_PADDING = 80;
const MIN_ZOOM_DURATION = 600;
const DURATION_PER_ZOOM_LEVEL = 200;
const LEAVES_FALLBACK_LIMIT = 10000;
const MERCATOR_MAX_LATITUDE = 85.051129;
const MERCATOR_TILE_SIZE = 512;

export function isValidPointPosition(lng: number | undefined, lat: number | undefined): boolean {
  return lng != null && lat != null && Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lat) <= 90;
}

export function withValidPointPositions(data: FeatureCollection): FeatureCollection {
  return {
    ...data,
    features: data.features.filter(
      (f) => f.geometry.type !== "Point" || isValidPointPosition(f.geometry.coordinates[0], f.geometry.coordinates[1])
    ),
  };
}

export function zoomAnimationDuration(map: MapboxMap, targetZoom: number | undefined): number {
  if (targetZoom == null) return MIN_ZOOM_DURATION;
  const delta = Math.abs(targetZoom - map.getZoom());
  return Math.max(MIN_ZOOM_DURATION, Math.round(delta * DURATION_PER_ZOOM_LEVEL));
}

function normalizedMercatorY(lat: number): number {
  const clampedLat = Math.min(MERCATOR_MAX_LATITUDE, Math.max(-MERCATOR_MAX_LATITUDE, lat));
  const latRad = (clampedLat * Math.PI) / 180;
  return (1 - Math.log(Math.tan(Math.PI / 4 + latRad / 2)) / Math.PI) / 2;
}

export function zoomToRevealPointAtOffset(lat: number, verticalOffset: number, viewportHeight: number): number {
  if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return Number.NEGATIVE_INFINITY;
  const mercatorY = normalizedMercatorY(lat);
  const halfHeight = viewportHeight / 2;
  const topConstrainedWorldSize = (halfHeight + verticalOffset) / mercatorY;
  const bottomConstrainedWorldSize = (halfHeight - verticalOffset) / (1 - mercatorY);
  const requiredWorldSize = Math.max(topConstrainedWorldSize, bottomConstrainedWorldSize);
  return Math.log2(requiredWorldSize / MERCATOR_TILE_SIZE);
}

// Feuille d'aperçu non mesurable pendant son animation → marqueur remonté via un ratio fixe du haut.
const ACTIVE_FEATURE_SMALL_SCREEN_MAX_WIDTH = 768;
const ACTIVE_FEATURE_MARKER_HEIGHT = 57;
const ACTIVE_FEATURE_TOP_RATIO = 0.3;

export function activeFeatureRevealOffset(mapHeight: number, viewportWidth: number): [number, number] {
  if (viewportWidth >= ACTIVE_FEATURE_SMALL_SCREEN_MAX_WIDTH) return [0, 0];
  const bodyCenterY = mapHeight * ACTIVE_FEATURE_TOP_RATIO;
  const markerTipY = bodyCenterY + ACTIVE_FEATURE_MARKER_HEIGHT / 2;
  return [0, markerTipY - mapHeight / 2];
}

export function fitBoundsAdaptive(map: MapboxMap, bounds: [[number, number], [number, number]], maxZoom: number): void {
  const targetZoom = map.cameraForBounds(bounds, { padding: FIT_PADDING })?.zoom;
  const clampedZoom = targetZoom == null ? maxZoom : Math.min(targetZoom, maxZoom);
  map.fitBounds(bounds, { padding: FIT_PADDING, maxZoom, duration: zoomAnimationDuration(map, clampedZoom) });
}

export function zoomToClusterLeaves(map: MapboxMap, source: GeoJSONSource, cluster: GeoJSON.Feature, maxZoom: number): void {
  if (cluster.geometry.type !== "Point") return;
  const clusterId = cluster.properties?.cluster_id as number | undefined;
  if (clusterId == null) return;
  const center = cluster.geometry.coordinates as [number, number];
  const limit = (cluster.properties?.point_count as number | undefined) ?? LEAVES_FALLBACK_LIMIT;

  source.getClusterLeaves(clusterId, limit, 0, (err, leaves) => {
    const bounds = err || !leaves ? undefined : leavesBounds(leaves);
    if (!bounds) {
      source.getClusterExpansionZoom(clusterId, (zoomErr, zoom) => {
        if (zoomErr || zoom == null) return;
        map.easeTo({ center, zoom, duration: zoomAnimationDuration(map, zoom) });
      });
      return;
    }
    fitBoundsAdaptive(map, bounds, maxZoom);
  });
}

function leavesBounds(leaves: GeoJSON.Feature[]): [[number, number], [number, number]] | undefined {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const leaf of leaves) {
    if (leaf.geometry.type !== "Point") continue;
    const [rawLng, lat] = leaf.geometry.coordinates as [number, number];
    if (!isValidPointPosition(rawLng, lat)) continue;
    const lng = wrapLng(rawLng);
    if (lng < west) west = lng;
    if (lng > east) east = lng;
    if (lat < south) south = lat;
    if (lat > north) north = lat;
  }
  if (west === Infinity) return undefined;
  return [
    [west, south],
    [east, north],
  ];
}
